import { createSelector } from 'reselect';
import { selectUserProjectDefaultSenderEmail } from 'core/userContext';
import type { BaseOption } from 'ui/selectv2/types';
import type { SendersReducerState } from './types';

export const getSendersState = (state: SendersReducerState) => state.settings.senders;
export const getAuthedEmails = (state: SendersReducerState) => getSendersState(state).authedEmails;
export const getSmsSenders = (state: SendersReducerState) => getSendersState(state).smsSenders;
export const getSmsMessageTypes = (state: SendersReducerState) =>
  getSendersState(state).smsMessageTypes;
export const getSmsEsps = (state: SendersReducerState) => getSendersState(state).smsEsps;
export const getValidSendDomains = (state: SendersReducerState) =>
  getSendersState(state).validSendDomains;

/**
 * selectBannersState
 * selector used to display a variety of potential warning banners based on the state
 * of the project's senders
 */
export const selectBannersState = createSelector(
  [getAuthedEmails, getSmsSenders, getSmsMessageTypes],
  (authedEmails, smsSenders, smsMessageTypes) => {
    const verifiedEmails = authedEmails.filter((email) => email.authorized);
    const nonLongCodeSmsSenders = smsSenders.filter((sender) => sender.type !== 'LongCode');

    return {
      showUnverifiedEmailsBanner: verifiedEmails.length === 0,
      showSmsSendersBanner: nonLongCodeSmsSenders.length === 0,
      smsMessageTypesBanner: smsMessageTypes.length === 0,
    };
  },
);

/**
 * selectAuthedEmailSendersTableData
 * selector used to format the data for the AuthedEmailsTable component.
 * It's made up of the authedEmails object, mixed together with whether or not
 * the email is the project's default sender.
 */
export const selectAuthedEmailSendersTableData = createSelector(
  [getAuthedEmails, selectUserProjectDefaultSenderEmail],
  (authedEmails, defaultSender) => {
    return authedEmails.map((email) => {
      return {
        ...email,
        isDefaultSender: email.email === defaultSender,
      };
    });
  },
);

/**
 * selectSmsSendersTableData
 * selector used to format the data for the SmsSendersTable component.
 * It's made up of the smsSenders object, mixed together with some related data
 * from the smsEsps and smsMessageTypes objects.
 */
export const selectSmsSendersTableData = createSelector(
  [getSmsSenders, getSmsEsps, getSmsMessageTypes],
  (smsSenders, smsEsps, smsMessageTypes) => {
    return smsSenders.map((sender) => {
      // set up sendingPlatform field
      const esp = smsEsps.find(({ id }) => id === sender.espAccountId);
      const espDescription = `${esp?.esp}:  ${esp?.name} (ID: ${esp?.id})`;

      // set up messageTypeDescription field
      const messageType = smsMessageTypes.find(
        ([smsMessageType]) => smsMessageType.id === sender.messageTypeId,
      );

      let messageTypeDescription = 'No binding';
      if (messageType) {
        const [smsMessageType, smsMessageChannel] = messageType;
        messageTypeDescription = `${smsMessageType.name} (${smsMessageChannel.channelType})`;
      }
      return {
        ...sender,
        espDescription,
        messageTypeDescription,
      };
    });
  },
);

/*
 * selectDefaultSenderEmailAsFormOption
 * selector used to display the project's default sender as a select option in the
 * DefaultSenderEmailForm component.
 */
export const selectDefaultSenderEmailAsFormOption = createSelector(
  [selectUserProjectDefaultSenderEmail],
  (defaultSenderEmail): BaseOption | null => {
    if (!defaultSenderEmail) {
      return null;
    }

    return {
      value: defaultSenderEmail,
      label: defaultSenderEmail,
    };
  },
);

/**
 * selectAuthedEmailsAsFormOptions
 * selector used to display a list of authorized emails as select options in the
 * DefaultSenderEmailForm component.
 */
export const selectAuthedEmailsAsFormOptions = createSelector([getAuthedEmails], (authedEmails) => {
  return authedEmails.map((email) => {
    return {
      value: email.email,
      label: email.email,
      // despite the name for the array returned from the backend,
      // "authedEmails" can contain unverified emails. But only verified emails
      // can be used as default sender emails, so we need to disable the ability
      // to select unverified ones.
      isDisabled: !email.authorized,
      // following the pattern used in the angular version of this page,
      // if the email cannot be used, we provide a useful little sufffix
      // to point out why that is the case.
      Suffix: email.authorized ? '' : 'Unverified',
    };
  });
});
